import { useMutation, useQuery } from '@apollo/client'
import {
  faBuilding,
  faChevronDown,
  faChevronRight,
  faDisplay,
  faMagnifyingGlass,
  faMapLocationDot,
  faPlus,
  faRemote
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAtom } from 'jotai'
import * as motion from 'motion/react-client'
import Link from 'next/link'
import { useEffect, useState } from 'react'

import {
  UserRole,
  type Customer,
  type Location,
  type MutationCreateLocationArgs
} from '@/graphql/generated-types'
import { customersAtom, customerAtom } from '@/shared-state/customers'
import { locationAtom } from '@/shared-state/location'
import { sessionAtom } from '@/shared-state/session'
import { logError } from '@/utils/log'
import { exitToLogin } from '@/utils/misc'

import { CREATE_LOCATION } from '../graphql/mutations'
import { GET_CUSTOMERS } from '../graphql/queries'

import InstallDevice from './InstallDevice'
import Modal from './Modal'
import UserSetting from './UserSetting'

function SkeletonLoading() {
  return (
    <div className="px-4 flex flex-col gap-4 animate-pulse">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="rounded-2xl bg-base-100 border border-base-300/40 overflow-hidden"
        >
          <div className="px-5 pt-5 pb-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-base-300/50" />
            <div className="flex flex-col gap-2">
              <div className="h-4 w-32 rounded bg-base-300/50" />
              <div className="h-3 w-16 rounded bg-base-300/40" />
            </div>
          </div>
          <div className="px-4 pb-4 flex flex-col gap-1.5">
            {[0, 1].map(j => (
              <div
                key={j}
                className="rounded-xl bg-base-200/60 border border-base-300/50 px-4 py-3.5 flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-base-300/50 shrink-0" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <div className="h-3.5 w-28 rounded bg-base-300/50" />
                  <div className="h-2.5 w-16 rounded bg-base-300/40" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center justify-center gap-4 py-20 px-8 text-center"
    >
      <div className="w-16 h-16 rounded-2xl bg-base-200 border border-base-300/40 flex items-center justify-center">
        <FontAwesomeIcon icon={faBuilding} className="text-2xl opacity-20" />
      </div>
      <div>
        <p className="text-lg spotonFont opacity-40">No customers yet</p>
        <p className="text-sm opacity-30 mt-1">
          Customers will appear here once added.
        </p>
      </div>
    </motion.div>
  )
}

function LocationCard({
  data,
  customer,
  index
}: {
  data: Location
  customer: Customer
  index: number
}) {
  const [, setSession] = useAtom(sessionAtom)
  const [, setCurrentCustomer] = useAtom(customerAtom)

  const { name, devices } = data
  const deviceCount = devices?.length ?? 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: 'easeOut' }}
    >
      <Link
        href="/location"
        onClick={() => {
          setCurrentCustomer(customer)
          setSession(prev => ({
            ...prev,
            locationId: data.locationId,
            customerId: customer.customerId
          }))
        }}
        className="group block rounded-xl bg-base-200/60 hover:bg-base-200 border border-base-300/50 hover:border-primary/30 transition-all duration-200"
      >
        <div className="px-4 py-3.5 flex justify-between items-center gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-base-300/60 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faMapLocationDot}
                className="text-xs text-warning/70"
              />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{name}</div>
              <div className="mt-0.5">
                <span
                  className={`badge badge-sm gap-1 ${deviceCount === 0 ? 'opacity-30' : 'badge-ghost'}`}
                >
                  <FontAwesomeIcon icon={faDisplay} className="text-[9px]" />
                  {deviceCount} {deviceCount === 1 ? 'device' : 'devices'}
                </span>
              </div>
            </div>
          </div>
          <FontAwesomeIcon
            icon={faChevronRight}
            className="text-xs opacity-20 group-hover:opacity-50 group-hover:translate-x-0.5 transition-all duration-200 shrink-0"
          />
        </div>
      </Link>
    </motion.div>
  )
}

function CustomerSection({
  data,
  index,
  isFiltering
}: {
  data: Customer
  index: number
  isFiltering: boolean
}) {
  const { name, locations } = data
  const [session, setSession] = useAtom(sessionAtom)
  const [, setLocation] = useAtom(locationAtom)
  const [, setCurrentCustomer] = useAtom(customerAtom)
  const [collapsed, setCollapsed] = useState(true)
  const [showNewLocation, setShowNewLocation] = useState(false)
  const [newLocationName, setNewLocationName] = useState('')
  const [createLocation, { loading: creating }] = useMutation<
    { createLocation: Location },
    MutationCreateLocationArgs
  >(CREATE_LOCATION, {
    refetchQueries: [{ query: GET_CUSTOMERS }],
    awaitRefetchQueries: true
  })

  if (!locations) return null

  const locationCount = locations.length
  const isExpanded = isFiltering || !collapsed

  async function handleCreateLocation() {
    if (!newLocationName.trim()) return
    try {
      await createLocation({
        variables: {
          input: { customerId: data.customerId, name: newLocationName.trim() }
        }
      })
      setShowNewLocation(false)
      setNewLocationName('')
    } catch (err) {
      logError('Create location failed:', err)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: 'easeOut' }}
      className="rounded-2xl bg-base-100 border border-base-300/40 overflow-hidden"
    >
      <div className="px-5 pt-5 pb-3">
        <button
          className="flex items-center gap-3 w-full text-left min-w-0"
          onClick={() => !isFiltering && setCollapsed(c => !c)}
        >
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl spotonFont uppercase tracking-wide text-primary truncate flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 shrink-0">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="text-sm text-primary"
                />
              </span>
              {name}
            </h2>
          </div>
          {!isFiltering && (
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`text-xs opacity-30 transition-transform duration-200 shrink-0 ${collapsed ? '-rotate-90' : ''}`}
            />
          )}
        </button>
      </div>

      {isExpanded && (
        <>
          {(session?.role === UserRole.Admin ||
            session?.role === UserRole.Customer) && (
            <div className="px-5 mt-3 mb-1">
              <Link
                className="btn btn-outline btn-sm gap-1 opacity-50 hover:opacity-100 text-sm px-4"
                href="/customer"
                onClick={() => {
                  setLocation(undefined)
                  setCurrentCustomer(data)
                  setSession(prev => ({
                    ...prev,
                    locationId: undefined,
                    customerId: data.customerId
                  }))
                }}
              >
                Manage shared media
                <FontAwesomeIcon icon={faChevronRight} className="text-[9px]" />
              </Link>
            </div>
          )}
          <div className="px-4 pb-2">
            <InstallDevice customerId={data.customerId} />
          </div>

          <div className="px-4 pb-4 flex flex-col gap-1.5">
            <div className="text-xs opacity-40 uppercase tracking-wider px-1 pb-0.5">
              {locationCount} {locationCount === 1 ? 'location' : 'locations'}
            </div>
            {locations.map((location, i) => (
              <LocationCard
                key={location.locationId}
                customer={data}
                data={location}
                index={i}
              />
            ))}

            {session?.role === UserRole.Admin && (
              <button
                className="mt-1 w-full rounded-xl border border-dashed border-base-300/70 hover:border-primary/40 hover:bg-base-200/40 transition-all duration-200 px-4 py-2.5 flex items-center gap-2 text-sm opacity-50 hover:opacity-90"
                onClick={() => setShowNewLocation(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                New location
              </button>
            )}
          </div>
        </>
      )}

      {showNewLocation && (
        <Modal
          showWrapper
          showCloseButton
          openModal={true}
          closeModal={() => {
            setShowNewLocation(false)
            setNewLocationName('')
          }}
          customClass="shadow-lg"
        >
          <h2 className="card-title spotonFont text-3xl mb-6">New location</h2>
          <div className="flex flex-col gap-4">
            <input
              type="text"
              className="input input-bordered w-full"
              placeholder="Location name"
              value={newLocationName}
              onChange={e => setNewLocationName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateLocation()}
              autoFocus
            />
            <button
              className="btn btn-primary"
              onClick={handleCreateLocation}
              disabled={!newLocationName.trim() || creating}
            >
              {creating ? 'Creating…' : 'Create location'}
            </button>
          </div>
        </Modal>
      )}
    </motion.div>
  )
}

export default function Customers() {
  const [customers, setCustomers] = useAtom(customersAtom)
  const [search, setSearch] = useState('')
  const { loading, error, data } = useQuery(GET_CUSTOMERS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  })

  const fetchedCustomers = data?.getCustomers

  useEffect(() => {
    if (fetchedCustomers) {
      setCustomers(fetchedCustomers)
    }
  }, [fetchedCustomers, setCustomers])

  if (loading && customers.length === 0) return <SkeletonLoading />
  if (error) {
    if (error.message === 'Unauthorized') return exitToLogin()
  }

  const query = search.trim().toLowerCase()
  const isFiltering = query.length > 0
  const filteredCustomers = isFiltering
    ? customers
        .map(c => ({
          ...c,
          locations:
            c.locations?.filter(l => l.name?.toLowerCase().includes(query)) ??
            []
        }))
        .filter(
          c =>
            c.name?.toLowerCase().includes(query) ||
            (c.locations?.length ?? 0) > 0
        )
    : customers

  return (
    <div className="relative mb-20">
      <div className="p-5 pb-3 flex justify-between items-center">
        <div className="w-10" />
        <h1 className="uppercase spotonFont text-3xl tracking-wider flex items-center gap-2">
          <FontAwesomeIcon icon={faRemote} className="text-2xl opacity-60" />
          Spoton Remote
        </h1>
        <UserSetting />
      </div>

      {customers.length > 1 && (
        <div className="px-4 mb-3">
          <div className="relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-30 pointer-events-none"
            />
            <input
              type="text"
              className="input input-bordered w-full pl-9 rounded-xl"
              placeholder="Search customers or locations…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      {customers.length === 0 ? (
        <EmptyState />
      ) : filteredCustomers.length === 0 ? (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-sm opacity-40 py-12"
        >
          No results for &ldquo;{search}&rdquo;
        </motion.p>
      ) : (
        <div className="px-4 pb-4 flex flex-col gap-4">
          {filteredCustomers.map((customer, i) => (
            <CustomerSection
              key={customer.customerId}
              data={customer}
              index={i}
              isFiltering={isFiltering}
            />
          ))}
        </div>
      )}
    </div>
  )
}
